import { useSound } from '@/contexts';
import { useCallback, useEffect, useState } from 'react';

export type ModalType = 'levelSelect' | 'shop' | 'settings' | 'levelComplete' | 'obstacleTutorial' | null;

export interface UseModalReturn {
  activeModal: ModalType;
  openModal: (modal: ModalType) => void;
  closeModal: () => void;
  isModalOpen: (modal: ModalType) => boolean;
}

export const useModal = (): UseModalReturn => {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const { playButtonClick } = useSound();

  const openModal = useCallback((modal: ModalType) => {
    playButtonClick();
    setActiveModal(modal);
  }, [playButtonClick]);

  const closeModal = useCallback(() => {
    playButtonClick();
    setActiveModal(null);
  }, [playButtonClick]);

  const isModalOpen = useCallback((modal: ModalType) => {
    return activeModal === modal;
  }, [activeModal]);

  // Global escape handler for modals
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && activeModal !== null) {
        event.preventDefault();
        closeModal();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [activeModal, closeModal]);

  return {
    activeModal,
    openModal,
    closeModal,
    isModalOpen,
  };
};
